import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

// Public
import Landing from './pages/Landing/Landing';

// Staff portal
import StaffLogin from './pages/Staff/Login';
import Dashboard from './pages/Staff/Dashboard';
import Tasks from './pages/Staff/Tasks';
import Customers from './pages/Staff/Customers';
import Inventory from './pages/Staff/Inventory';
import BSNL from './pages/Staff/BSNL';
import CallLogs from './pages/Staff/CallLogs';
import Attendance from './pages/Staff/Attendance';
import Quotations from './pages/Staff/Quotations';
import Tickets from './pages/Staff/Tickets';
import Documents from './pages/Staff/Documents';
import Team from './pages/Staff/Team';
import Notifications from './pages/Staff/Notifications';
import Bookings from './pages/Staff/Bookings';
import Billing from './pages/Staff/Billing';

import { useStaffAuthStore } from './store/staffAuthStore';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            retry: 1,
            staleTime: 2 * 60 * 1000,
        },
    },
});

// Redirect to login if not authenticated
const StaffRoute = ({ children }) => {
    const { user } = useStaffAuthStore();
    return user ? children : <Navigate to="/staff" replace />;
};

// Redirect to dashboard if already logged in
const StaffPublic = ({ children }) => {
    const { user } = useStaffAuthStore();
    return user ? <Navigate to="/staff/dashboard" replace /> : children;
};

function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <Router>
                <Routes>
                    {/* ── Public landing ── */}
                    <Route path="/" element={<Landing />} />

                    {/* ── Staff portal ── */}
                    <Route path="/staff" element={<StaffPublic><StaffLogin /></StaffPublic>} />
                    <Route path="/staff/dashboard" element={<StaffRoute><Dashboard /></StaffRoute>} />
                    <Route path="/staff/tasks" element={<StaffRoute><Tasks /></StaffRoute>} />
                    <Route path="/staff/customers" element={<StaffRoute><Customers /></StaffRoute>} />
                    <Route path="/staff/inventory" element={<StaffRoute><Inventory /></StaffRoute>} />
                    <Route path="/staff/bsnl" element={<StaffRoute><BSNL /></StaffRoute>} />
                    <Route path="/staff/call-logs" element={<StaffRoute><CallLogs /></StaffRoute>} />
                    <Route path="/staff/attendance" element={<StaffRoute><Attendance /></StaffRoute>} />
                    <Route path="/staff/quotations" element={<StaffRoute><Quotations /></StaffRoute>} />
                    <Route path="/staff/billing" element={<StaffRoute><Billing /></StaffRoute>} />
                    <Route path="/staff/tickets" element={<StaffRoute><Tickets /></StaffRoute>} />
                    <Route path="/staff/bookings" element={<StaffRoute><Bookings /></StaffRoute>} />
                    <Route path="/staff/documents" element={<StaffRoute><Documents /></StaffRoute>} />
                    <Route path="/staff/team" element={<StaffRoute><Team /></StaffRoute>} />
                    <Route path="/staff/notify" element={<StaffRoute><Notifications /></StaffRoute>} />

                    {/* ── Catch all ── */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </Router>

            <Toaster
                position="top-right"
                toastOptions={{
                    duration: 3000,
                    style: {
                        background: '#1e293b',
                        color: '#f8fafc',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: '12px',
                        fontSize: '13px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                    },
                    success: { iconTheme: { primary: '#22c55e', secondary: '#1e293b' } },
                    error: { iconTheme: { primary: '#ef4444', secondary: '#1e293b' } },
                }}
            />
        </QueryClientProvider>
    );
}

export default App;
